import IframeResizer from 'iframe-resizer-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import PageHeader from '../Components/Shared/PageHeader';
import { getTitlePrefix } from '../config/env';
import { usePrimaryLocation } from '../hooks/useLocations';
import { useOrganization } from '../hooks/useOrganizations';
import { useUser } from '../hooks/useUser';

interface EmbeddedFormPageProps {
  // titleKey must exist in both en/translation.json and es/translation.json
  titleKey: string;
  description?: string;
  descriptionKey?: string;
  formUrl: string;
  // When true, also prefill the director's first/last name + phone (from their profile) and
  // the school's street address. Opt-in so we don't push contact PII into other embedded forms.
  // Requires matching URL-parameter prefill names configured on the Fillout form.
  prefillDirectorContact?: boolean;
}

const EmbeddedFormPage = ({
  titleKey,
  description,
  descriptionKey,
  formUrl: baseFormUrl,
  prefillDirectorContact = false,
}: EmbeddedFormPageProps) => {
  const { t } = useTranslation();
  const user = useUser();
  const organization = useOrganization();
  const primaryLocation = usePrimaryLocation();
  const iframeRef = useRef<any>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);

  const title = t(titleKey);
  const translatedDescription = descriptionKey ? t(descriptionKey) : description;

  const formUrl = useMemo(() => {
    const url = new URL(baseFormUrl);
    if (user?.email) url.searchParams.set('email', user.email);
    if (user?.displayName) url.searchParams.set('name', user.displayName);
    if (organization?.id) url.searchParams.set('organizationId', organization.id);
    if (organization?.name) url.searchParams.set('organizationName', organization.name);
    if (primaryLocation?.city) url.searchParams.set('city', primaryLocation.city);
    if (primaryLocation?.state) url.searchParams.set('state', primaryLocation.state);
    if (prefillDirectorContact) {
      // Director's name + phone come from their profile (state.user). The director profile has
      // no address field, so we fall back to the school's street address (address1).
      if (user?.firstName) url.searchParams.set('firstName', user.firstName);
      if (user?.lastName) url.searchParams.set('lastName', user.lastName);
      if (user?.phone) url.searchParams.set('phone', user.phone);
      if (primaryLocation?.address1) url.searchParams.set('address', primaryLocation.address1);
    }
    return url.toString();
  }, [
    baseFormUrl,
    user?.email,
    user?.displayName,
    organization?.id,
    organization?.name,
    primaryLocation?.city,
    primaryLocation?.state,
    prefillDirectorContact,
    user?.firstName,
    user?.lastName,
    user?.phone,
    primaryLocation?.address1,
  ]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!iframeLoaded) setIframeError(true);
    }, 10000);
    return () => clearTimeout(timeoutId);
  }, [iframeLoaded]);

  return (
    <>
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <Helmet>
          <title>
            {getTitlePrefix()}
            {`${title} - Wonderschool`}
          </title>
        </Helmet>
        <PageHeader pageName={title} classes="embedded-form-page" />

        <div className="mx-auto max-w-5xl">
          <div className="mb-10">
            <p className="text-base leading-relaxed text-gray-700 sm:text-lg">{translatedDescription}</p>
          </div>

          <div className="overflow-hidden rounded-lg bg-white p-6 shadow-md sm:rounded-lg">
            <div
              className="fillout-form-container relative rounded-lg"
              style={{ minHeight: '600px' }}
              role="region"
              aria-label={t('embeddedForm.regionAriaLabel', { title })}
            >
              {iframeError && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-gray-50">
                  <div className="p-4 text-center">
                    <p className="mb-4 text-sm font-semibold text-red-600">{t('embeddedForm.loadError')}</p>
                    <a
                      href={formUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      {t('embeddedForm.openInNewTab')}
                    </a>
                  </div>
                </div>
              )}
              <IframeResizer
                forwardRef={iframeRef}
                src={formUrl}
                style={{ border: 'none', minHeight: '600px' }}
                scrolling="omit"
                minHeight={600}
                maxHeight={2160}
                heightCalculationMethod="lowestElement"
                width="100%"
                onLoad={() => setIframeLoaded(true)}
                onResized={() => setIframeLoaded(true)}
                checkOrigin={false}
                title={t('embeddedForm.iframeTitle', { title })}
                aria-label={t('embeddedForm.regionAriaLabel', { title })}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmbeddedFormPage;
